import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import {
  ref,
  onBeforeMount,
} from "vue";
import { useSchoolStore } from "@/stores/schoolStore";
import { useSubjectStore } from "@/stores/subjectStore";
import {
  InitialSchoolStructure,
  NewClassGroup,
  NewClassTeacher,
} from "@/interfaces/school-structure.interface";
import i18n from "@/plugins/i18n";
import SchoolStructureIntro from "@/modules/main/components/SchoolStructureIntro.vue";
import StepProgressIndicator from "@/modules/main/components/StepProgressIndicator.vue";
import AddTeachersComponent from "@/modules/main/components/AddTeachersComponent.vue";
import AddGradesComponent from "@/modules/main/components/AddGradesComponent.vue";
import AssignNewTeachersToClasses from "@/modules/main/components/AssignNewTeachersToClasses.vue";
import ReviewTeacherAssignedToClasses from "@/modules/main/components/ReviewTeacherAssignedToClasses.vue";
import AddGradesClassesModals from "@/modules/main/components/modals/AddGradesClassesModals.vue";
import { useFilterStore } from "@/stores/filterStore";
import { defaultGrades, generateUniqueUID } from "@/modules/main/components/constants/defaults";


export default /*#__PURE__*/_defineComponent({
  __name: 'TeacherOnboardingComponent',
  setup(__props) {

const step = ref(0);
const showSendDetails = ref(false);
const teachers = ref<NewClassTeacher[]>([]);
const classGroups = ref<NewClassGroup[]>([]);
const gradeLevels = ref<number[]>([]);
const showModal = ref(false);
const errorMessage = ref("");
const selectedGrades = ref(defaultGrades());

// Stores
const filterStore = useFilterStore();
const schoolStore = useSchoolStore();
const subjectStore = useSubjectStore();

const handleUpdateTeachers = (updatedTeachers: NewClassTeacher[]) => {
  teachers.value = updatedTeachers;
};

// Before mount hook
onBeforeMount(() => {
  teachers.value =
    schoolStore?.initialSchoolStructure?.teachers &&
      schoolStore?.initialSchoolStructure?.teachers.length > 0
      ? schoolStore.initialSchoolStructure.teachers
      : [];
  step.value = schoolStore?.step || 0;
  selectedGrades.value =
    schoolStore?.SelectedGrades.length > 0
      ? schoolStore.SelectedGrades
      : selectedGrades.value;
  classGroups.value = schoolStore?.initialSchoolStructure?.classGroups || [];
});

const showSendDetailsModal = () => {
  //final Step
  if (teachers.value.length > 0) {
    step.value = 5;
  }
  showSendDetails.value = true;
};

const sendLogin = async () => {
  //Create the initial school structure object
  const initialSchoolStructure = {
    id: filterStore.selectedFilters.school,
    teachers: teachers.value,
    classGroups: classGroups.value,
  } as InitialSchoolStructure;

  //only show the modal if teachers are present.
  if (teachers.value.length > 0) {
    // Update the school structure in the store
    schoolStore.updateEnableSentModal(true);
  }

  await schoolStore.initialiseSchoolStructure(initialSchoolStructure);
};

const handleNext = () => {

  // validate class name, subject and class language
  if (step.value == 2) {

     // VALIDATION 1: Check if class name is empty for selected grades
     const invalidClassName = selectedGrades.value.some(grade =>
      grade.selected && grade.classes.some(cls => cls.name.trim() === "")
    );

    if (invalidClassName) {
      console.log("One or more classes are missing a class name.");
      errorMessage.value = i18n.global.t('add_grades_class_component.step_2.validation.className');
      showModal.value = true;
      return; 
    }

    // VALIDATION 2: Check if both English and Maths are false for selected grades
     const missingSubject = selectedGrades.value.some(grade =>
      grade.selected && grade.classes.some(cls => !cls.english && !cls.maths)
    );

    if (missingSubject) {
      console.log("One or more classes are missing a selected subject.");
      errorMessage.value = i18n.global.t('add_grades_class_component.step_2.validation.subject');
      showModal.value = true;
      return; 
    }

    // VALIDATION 3: Check if preferredLanguage is empty for selected grades
     const invalidLanguage = selectedGrades.value.some(grade =>
      grade.selected && grade.classes.some(cls => cls.preferredLanguage.trim() === "")
    );

    if (invalidLanguage) {
      console.log("One or more classes are missing a preferred language.");
      errorMessage.value = i18n.global.t('add_grades_class_component.step_2.validation.language');
      showModal.value = true;
      return; 
    }

    // VALIDATION 4: Check if selected grades have zero classes
    const emptyClasses = selectedGrades.value.some(grade =>
      grade.selected && grade.classes.length === 0
    );

    if (emptyClasses) {
      console.log("One or more selected grades have no assigned classes.");
      errorMessage.value = i18n.global.t('add_grades_class_component.step_2.validation.grade');
      showModal.value = true;
      return;
    }
  }

  if (step.value == 2 && teachers.value.length === 0) {
    schoolStore.updateGrades(selectedGrades.value);

    schoolStore.updateClassGroups([]);
    schoolStore.updateTeachers([]);

    // Generate the class group structure
    generateClassGroupStructure();

    // Update the teachers and classGroups arrays in the store
    schoolStore.updateTeachers(teachers.value);
    schoolStore.updateClassGroups(classGroups.value);

    showSendDetailsModal();
  } else {

    step.value++;

    if (step.value == 2) {
      schoolStore.updateTeachers(teachers.value);
    }

    if (step.value == 3) {
      schoolStore.updateGrades(selectedGrades.value);
    }

    //calling classGroupStructure
    if (step.value > 3) {
      // Clear the teachers and classGroups arrays in the store
      schoolStore.updateClassGroups([]);
      schoolStore.updateTeachers([]);

      // Generate the class group structure
      generateClassGroupStructure();

      // Update the teachers and classGroups arrays in the store
      schoolStore.updateTeachers(teachers.value);
      schoolStore.updateClassGroups(classGroups.value);
    }

    //update current step to state.
    schoolStore.updateCurrentStep(step.value);
  }
};

const handleBack = () => {
  step.value--;
  schoolStore.updateCurrentStep(step.value);
};

// Function to generate the class group structure
const generateClassGroupStructure = () => {
  // Clear gradeLevels
  gradeLevels.value = [];

  //clear all their classes so that we don't push addional classes.
  for (let i = 0; i < teachers.value.length; i++) {
    teachers.value[i].classes = [];
  }

  classGroups.value = selectedGrades.value
    .filter((grade) => grade.selected)
    .map((grade) => {
      return grade.classes
        .map((c) => {
          // Find the subjects from the store based on the name
          const subjectEnglish = c.english
            ? subjectStore.subjects.find(
              (subject) => subject.name === "English"
            )?.id
            : "";
          const subjectMaths = c.maths
            ? subjectStore.subjects.find((subject) => subject.name === "Maths")
              ?.id
            : "";

          let result = [];

          // Update gradeLevels only if it is not already in the array
          const gradeLevel = parseInt(grade.grade);
          if (!gradeLevels.value.includes(gradeLevel)) {
            gradeLevels.value.push(gradeLevel);
          }

          if (subjectEnglish && !subjectMaths) {
            result.push({
              tempId: generateUniqueUID(),
              name: c.name,
              teacher: (c.englishTeachers as { tempId: string })?.tempId,
              subject: subjectEnglish,
              gradeLevel: gradeLevel,
              preferredLanguage: c.preferredLanguage,
            });
          } else if (subjectMaths && !subjectEnglish) {
            result.push({
              tempId: generateUniqueUID(),
              name: c.name,
              teacher: (c.mathsTeachers as { tempId: string })?.tempId,
              subject: subjectMaths,
              gradeLevel: gradeLevel,
              preferredLanguage: c.preferredLanguage,
            });
          }

          // Loop through the result array and update each teacher's classes
          for (let i = 0; i < result.length; i++) {
            // Find the teacher in the teachers array using the tempId
            let teacher = teachers.value.find(
              (t) => t.tempId === result[i].teacher
            );

            if (teacher) {
              // Check if the class is already in the teacher's classes array (to avoid duplicates)
              const classExists = teacher.classes.some(
                (clsId) => clsId === result[i].tempId
              );

              if (!classExists) {
                // If the class does not exist, push the class tempId into the teacher's classes array
                teacher.classes.push(result[i].tempId);
              }
            }
          }

          // Return the array of objects
          return result;
        })
        .flat();
    })
    .flat();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (step.value == 0)
      ? (_openBlock(), _createBlock(SchoolStructureIntro, {
          key: 0,
          step: step.value,
          "onUpdate:step": _cache[0] || (_cache[0] = ($event: any) => (step.value = $event)),
          onNextStep: _cache[1] || (_cache[1] = ($event: any) => (step.value = step.value + 1))
        }, null, 8, ["step"]))
      : _createCommentVNode("", true),
    (step.value > 0)
      ? (_openBlock(), _createBlock(StepProgressIndicator, {
          key: 1,
          step: step.value
        }, null, 8, ["step"]))
      : _createCommentVNode("", true),
    (step.value == 1)
      ? (_openBlock(), _createBlock(AddTeachersComponent, {
          key: 2,
          schoolStore: _unref(schoolStore),
          filterStore: _unref(filterStore),
          step: step.value,
          teachersArray: teachers.value,
          "onUpdate:teachers": handleUpdateTeachers,
          onNextStep: handleNext
        }, null, 8, ["schoolStore", "filterStore", "step", "teachersArray"]))
      : _createCommentVNode("", true),
    (step.value == 2)
      ? (_openBlock(), _createBlock(AddGradesComponent, {
          key: 3,
          teachers: teachers.value,
          newGrades: selectedGrades.value,
          step: step.value,
          onHandleBack: handleBack,
          onHandleNext: handleNext
        }, null, 8, ["teachers", "newGrades", "step"]))
      : _createCommentVNode("", true),
    (step.value == 3)
      ? (_openBlock(), _createBlock(AssignNewTeachersToClasses, {
          key: 4,
          grades: selectedGrades.value,
          teacherList: teachers.value,
          onHandleBack: handleBack,
          onHandleNext: handleNext
        }, null, 8, ["grades", "teacherList"]))
      : _createCommentVNode("", true),
    (step.value >= 4)
      ? (_openBlock(), _createBlock(ReviewTeacherAssignedToClasses, {
          key: 5,
          subjectStore: _unref(subjectStore),
          step: step.value,
          teachersArray: teachers.value,
          classGroupsArray: classGroups.value,
          onHandleBack: handleBack,
          onHandleReset: _cache[2] || (_cache[2] = ($event: any) => (step.value=1)),
          onShowSendModal: showSendDetailsModal
        }, null, 8, ["subjectStore", "step", "teachersArray", "classGroupsArray"]))
      : _createCommentVNode("", true),
    _createVNode(AddGradesClassesModals, {
      showSendDetails: showSendDetails.value,
      "onUpdate:showSendDetails": _cache[3] || (_cache[3] = ($event: any) => ((showSendDetails).value = $event)),
      showModal: showModal.value,
      "onUpdate:showModal": _cache[4] || (_cache[4] = ($event: any) => ((showModal).value = $event)),
      schoolStore: _unref(schoolStore),
      errorMessage: errorMessage.value,
      teachers: teachers.value,
      onSendLogin: sendLogin
    }, null, 8, ["showSendDetails", "showModal", "schoolStore", "errorMessage", "teachers"])
  ], 64))
}
}

})